import { useState } from 'react';
import { Link } from 'react-router';
import { AlertCircle, MoreHorizontal, Pencil, Plus, Radio, Trash2, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useRegions } from '../../hooks/useRegions';
import { useDivisions } from '../../hooks/useDivisions';
import { useCredentials } from '../../hooks/useCredentials';
import {
  CONTROLLER_TYPES,
  SITES_TO_REVIEW_PARTS,
  SYNC_HEALTH_STYLE,
  connectionOf,
  controllerTypeInfo,
  controllerTypeLabel,
  isDirectoryType,
  syncHealthLabel,
  useControllers,
  useCreateController,
  useDeleteController,
  useUpdateController,
  type Controller,
} from '../../hooks/useControllers';
import ConfirmationModal from '../../components/ConfirmationModal';
import CredentialSelect from '../../components/CredentialSelect';
import { UniFiKeySteps, type UniFiKeySource } from '../../components/UniFiKeyPanel';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

const NO_SELECTION = '__none__';

function plural(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

/** The address to open a controller at, once `baseUrl` is one. */
function originOf(baseUrl: string): string | undefined {
  try {
    const url = new URL(baseUrl.trim());
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.origin : undefined;
  } catch {
    return undefined;
  }
}

export default function Controllers() {
  const { user } = useAuth();
  const organizationId = user?.organizationId;

  const { data: controllers, isLoading, isError } = useControllers(organizationId);
  const { data: regions } = useRegions(organizationId);
  const { data: divisions } = useDivisions(organizationId);
  const { data: credentials } = useCredentials(organizationId);

  const createController = useCreateController(organizationId);
  const deleteController = useDeleteController(organizationId);

  // Create/edit sheet state.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<Controller | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [type, setType] = useState('unifi');
  const [baseUrl, setBaseUrl] = useState('');
  const [site, setSite] = useState('');
  const [credentialId, setCredentialId] = useState('');
  const [verifyTls, setVerifyTls] = useState(false);
  const [regionId, setRegionId] = useState('');
  const [divisionId, setDivisionId] = useState(NO_SELECTION);
  const [description, setDescription] = useState('');

  // Delete state.
  const [deleting, setDeleting] = useState<Controller | null>(null);

  const updateController = useUpdateController(editing?.id ?? '', organizationId);

  // What the chosen type needs from the form. A directory (UniFi Site Manager)
  // is a cloud API at one fixed address, signs in with an API key only, always
  // verifies TLS, and has no site of its own.
  const typeInfo = controllerTypeInfo(type);
  const isDirectory = typeInfo?.directory === true;
  const allowedCredentialTypes = typeInfo?.credentialTypes ?? ['usernamePassword', 'apiKey'];

  // A UniFi API key is made in UniFi, so "+ New credential…" walks through it.
  // Which kind it must be follows from how the controller is reached: a
  // directory reads Site Manager, a relayed controller one site through UniFi's
  // cloud, and any other the console itself on the LAN.
  const isUniFi = type === 'unifi' || type === 'unifi_site_manager';
  const keySource: UniFiKeySource = isDirectory
    ? { kind: 'site-manager' }
    : connectionOf(baseUrl) === 'cloud'
      ? { kind: 'cloud-site', site: site.trim() || undefined }
      : { kind: 'console', href: originOf(baseUrl), hrefLabel: 'your controller' };

  const chooseType = (next: string): void => {
    const nextInfo = controllerTypeInfo(next);
    const prevFixed = typeInfo?.fixedBaseUrl;
    setType(next);
    if (nextInfo?.fixedBaseUrl) {
      setBaseUrl(nextInfo.fixedBaseUrl);
      setVerifyTls(true);
      setSite('');
    } else if (prevFixed && baseUrl === prevFixed) {
      setBaseUrl('');
      setVerifyTls(false);
    }
    const chosen = credentials?.find((cr) => cr.id === credentialId);
    if (chosen && nextInfo && !nextInfo.credentialTypes.includes(chosen.type)) {
      setCredentialId('');
    }
  };

  // A credential lives on its own region's gateways, so one chosen for another
  // region goes when the region changes.
  const chooseRegion = (next: string): void => {
    setRegionId(next);
    const chosen = credentials?.find((cr) => cr.id === credentialId);
    if (chosen && chosen.regionId !== next) {
      setCredentialId('');
    }
  };

  // When creating and there's exactly one region, preselect it so the user
  // doesn't have to. Runs once regions load (and after openCreate clears it).
  if (!editing && !regionId && regions && regions.length === 1) {
    setRegionId(regions[0].id);
  }

  const resetForm = (): void => {
    setName('');
    setType('unifi');
    setBaseUrl('');
    setSite('');
    setCredentialId('');
    setVerifyTls(false);
    setRegionId('');
    setDivisionId(NO_SELECTION);
    setDescription('');
    setSubmitError(null);
  };

  const openCreate = (): void => {
    setEditing(null);
    resetForm();
    setSheetOpen(true);
  };

  const openEdit = (c: Controller): void => {
    setEditing(c);
    setName(c.name);
    setType(c.type);
    setBaseUrl(c.baseUrl);
    setSite(c.site ?? '');
    setCredentialId(c.credentialId);
    setVerifyTls(c.verifyTls);
    setRegionId(c.regionId);
    setDivisionId(c.divisionId ?? NO_SELECTION);
    setDescription(c.description ?? '');
    setSubmitError(null);
    setSheetOpen(true);
  };

  const handleSubmit = (e: React.SubmitEvent): void => {
    e.preventDefault();
    setSubmitError(null);
    if (!name || !baseUrl || !credentialId) {
      setSubmitError('Name, controller URL and credential are required.');
      return;
    }
    if (!editing && !regionId) {
      setSubmitError('Region is required.');
      return;
    }
    const division = divisionId === NO_SELECTION ? null : divisionId;

    if (editing) {
      updateController.mutate(
        { name, baseUrl, site: site || null, credentialId, verifyTls, description: description || null },
        {
          onSuccess: () => {
            setSheetOpen(false);
          },
          onError: (err) => {
            setSubmitError(err.message || 'Failed to update controller.');
          },
        },
      );
    } else {
      createController.mutate(
        {
          name,
          type,
          baseUrl,
          site: site || null,
          credentialId,
          verifyTls,
          description: description || null,
          regionId,
          divisionId: division,
        },
        {
          onSuccess: () => {
            setSheetOpen(false);
          },
          onError: (err) => {
            setSubmitError(err.message || 'Failed to create controller.');
          },
        },
      );
    }
  };

  const handleDelete = (): void => {
    if (!deleting) return;
    deleteController.mutate(deleting.id, {
      onSuccess: () => {
        setDeleting(null);
      },
    });
  };

  const columns: readonly ResponsiveTableColumn<Controller>[] = [
    {
      key: 'name',
      label: 'Name',
      hideMobileLabel: true,
      render: (c) => (
        <div className="min-w-0">
          <Link to={`/controllers/${c.id}`} className="font-medium hover:underline">
            {c.name}
          </Link>
          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{c.baseUrl}</p>
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      render: (c) => <span className="text-sm">{controllerTypeLabel(c.type)}</span>,
    },
    {
      key: 'syncHealth',
      label: 'Status',
      render: (c) => (
        <Badge
          className={cn(
            'border-0 font-normal',
            SYNC_HEALTH_STYLE[c.syncHealth] ?? 'bg-muted text-muted-foreground',
          )}
        >
          {syncHealthLabel(c.syncHealth)}
        </Badge>
      ),
    },
    {
      key: 'deviceCount',
      label: 'Inventory',
      render: (c) => {
        if (!isDirectoryType(c.type)) {
          return <span className="text-sm tabular-nums">{plural(c.deviceCount, 'device', 'devices')}</span>;
        }
        // A directory's "to review": decisions nobody has made yet, each part
        // opening the Sites table narrowed to exactly what it counted.
        const review = c.sitesToReview;
        const parts = review ? SITES_TO_REVIEW_PARTS.filter((p) => review[p.key] > 0) : [];
        return (
          <div className="text-sm tabular-nums">
            <p>{plural(c.siteCount ?? 0, 'site', 'sites')}</p>
            {review && parts.length ? (
              <p className="text-xs mt-0.5 flex flex-wrap gap-x-1.5">
                {parts.map((p, i) => (
                  <Link key={p.key} to={`/controllers/${c.id}?sites=${p.filter}`} className="text-primary hover:underline">
                    {String(review[p.key])} {p.label}
                    {i < parts.length - 1 ? ' ·' : ''}
                  </Link>
                ))}
              </p>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Controllers"
        subtitle="Connect a UniFi, Omada, or other vendor controller to import its devices, credentials, and wireless telemetry automatically — or a UniFi Site Manager account to discover every console it manages."
        primary={
          <Button onClick={openCreate} className="gap-1.5">
            <Plus className="w-4 h-4" />
            Add controller
          </Button>
        }
      />

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading controllers…</p>
      ) : isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to load controllers.</AlertDescription>
        </Alert>
      ) : !controllers || controllers.length === 0 ? (
        <EmptyState
          variant="no-data"
          icon={Radio}
          title="No controllers yet"
          description="Add your UniFi or Omada controller and we'll pull in its devices and clients."
          action={
            <Button onClick={openCreate} className="gap-1.5">
              <Plus className="w-4 h-4" />
              Add controller
            </Button>
          }
        />
      ) : (
        <ResponsiveTable<Controller>
          columns={columns}
          rows={controllers}
          rowKey={(r) => r.id}
          rowActions={(c) => (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  aria-label={`Actions for ${c.name}`}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <DropdownMenuItem onClick={() => { openEdit(c); }} className="gap-2">
                  <Pencil className="w-4 h-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => { setDeleting(c); }}
                  className="gap-2 text-sev-1 focus:text-sev-1"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        />
      )}

      <AppSheet
        open={sheetOpen}
        onOpenChange={(open) => {
          if (!open) setSheetOpen(false);
        }}
        title={editing ? 'Edit controller' : 'Add controller'}
        size="md"
      >
        <AppSheet.Body>
          {submitError ? (
            <Alert variant="destructive" className="mb-4 relative">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{submitError}</AlertDescription>
              <button
                type="button"
                onClick={() => { setSubmitError(null); }}
                className="absolute top-2 right-2"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </Alert>
          ) : null}
          <form id="controller-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="c-name">Name</Label>
              <Input
                id="c-name"
                value={name}
                onChange={(e) => { setName(e.target.value); }}
                placeholder="e.g., HQ UniFi"
                required
              />
            </div>

            {!editing ? (
              <div className="space-y-1.5">
                <Label htmlFor="c-type">Controller type</Label>
                <Select value={type} onValueChange={chooseType}>
                  <SelectTrigger id="c-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTROLLER_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value} disabled={!t.implemented}>
                        {t.label}
                        {t.implemented ? '' : ' (coming soon)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {isDirectory ? (
              <p className="text-sm text-muted-foreground">
                Lists every console and site this UniFi account can see, so you can match each
                one to a customer. It imports no devices itself.
              </p>
            ) : (
              <>
                {!editing ? (
                  <p className="text-sm text-muted-foreground">
                    Adding this controller brings all of its devices under management.
                  </p>
                ) : null}
                <div className="space-y-1.5">
                  <Label htmlFor="c-url">Controller URL</Label>
                  <Input
                    id="c-url"
                    value={baseUrl}
                    onChange={(e) => { setBaseUrl(e.target.value); }}
                    placeholder="https://10.0.0.5:8443"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Self-hosted controllers usually run on port 8443; UniFi OS consoles on 443.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="c-site">Site (optional)</Label>
                  <Input
                    id="c-site"
                    value={site}
                    onChange={(e) => { setSite(e.target.value); }}
                    placeholder="default"
                  />
                </div>
              </>
            )}

            {!editing ? (
              <div className="space-y-1.5">
                <Label htmlFor="c-region">Region</Label>
                <Select value={regionId} onValueChange={chooseRegion} required>
                  <SelectTrigger id="c-region">
                    <SelectValue placeholder="Select a region" />
                  </SelectTrigger>
                  <SelectContent>
                    {(regions ?? []).map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isDirectory ? (
                  <p className="text-xs text-muted-foreground">
                    Use your own region, not a customer&apos;s: this key can read every
                    customer&apos;s console, and a gateway in this region will hold it.
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="c-cred">{isDirectory ? 'Site Manager API key' : 'Admin credential'}</Label>
              <CredentialSelect
                id="c-cred"
                value={credentialId || null}
                onChange={(id) => { setCredentialId(id ?? ''); }}
                regionId={regionId}
                types={allowedCredentialTypes}
                placeholder={
                  isDirectory ? 'Select an API key credential' : 'Select a login or API key credential'
                }
                guidance={isUniFi ? { apiKey: <UniFiKeySteps source={keySource} /> } : undefined}
              />
              <p className="text-xs text-muted-foreground">
                {isDirectory
                  ? 'An API key created at unifi.ui.com under API. It is copied only to the gateways in the region you choose above.'
                  : "The controller's admin login, or an API key created on the controller. The gateway uses it to read devices and the SSH credentials the controller manages."}
              </p>
            </div>

            {!editing && divisions && divisions.length > 0 ? (
              <div className="space-y-1.5">
                <Label htmlFor="c-division">Division (optional)</Label>
                <Select value={divisionId} onValueChange={setDivisionId}>
                  <SelectTrigger id="c-division">
                    <SelectValue placeholder="Org-level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_SELECTION}>Org-level</SelectItem>
                    {divisions.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {isDirectory ? null : (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="c-tls"
                  checked={verifyTls}
                  onCheckedChange={(v) => { setVerifyTls(v === true); }}
                />
                <Label htmlFor="c-tls" className="font-normal">
                  Verify TLS certificate (leave off for self-signed on-prem controllers)
                </Label>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="c-desc">Description (optional)</Label>
              <Textarea
                id="c-desc"
                value={description}
                onChange={(e) => { setDescription(e.target.value); }}
                rows={2}
              />
            </div>
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button variant="outline" type="button" onClick={() => { setSheetOpen(false); }}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="controller-form"
            disabled={createController.isPending || updateController.isPending}
          >
            {createController.isPending || updateController.isPending
              ? 'Saving…'
              : editing
                ? 'Save changes'
                : 'Add controller'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <ConfirmationModal
        isActive={deleting !== null}
        title="Delete controller"
        message={
          deleting
            ? `Delete "${deleting.name}"? The gateway will stop syncing it. Devices and credentials it already imported are kept.`
            : ''
        }
        confirmText="Delete controller"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => { setDeleting(null); }}
        isLoading={deleteController.isPending}
        isDanger={true}
      />
    </PageContainer>
  );
}
